import axios from 'axios';
import { padStart } from 'lodash';
import auth from '@client/components/auth';
import { Download, Media } from 'generated/graphql';
import { toImgSrc, toVideoSrc } from '@client/pages/CustomProgramPage/helpers';
import _ from 'lodash';
import { TypedValue, ValueMap } from '@client/new/helpers/types';
import { GQLVideo, LibraryVideo } from './videos.domain';
import { CommonMedia, commonMediaKeys, FullMedia } from './GQLTypes';

export type EditParams = {
  removeAudio?: boolean;
  startTime?: number;
  endTime?: number;
  thumbnailTimestamp?: number;
};

export function checkVideoMetadata(file: File | string) {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = function () {
        resolve(this);
      };

      video.onerror = function () {
        reject('Invalid video. Please select a video file.');
      };

      video.src =
        typeof file === 'string' ? file : window.URL.createObjectURL(file);
      return video;
    } catch (e) {
      reject(e);
    }
  });
}

export function downloadURI(uri: string, name: string) {
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = uri;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function secondsToHHMMSS(seconds: number): string {
  seconds = Math.round(seconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const formattedTime =
    padStart(hours.toString(), 2, '0') +
    ':' +
    padStart(minutes.toString(), 2, '0') +
    ':' +
    padStart(remainingSeconds.toString(), 2, '0');
  return formattedTime;
}

const getDownloadLink = (download: Download) => {
  return download?.high?.link || download?.medium?.link || download.low.link;
};

const gqlToLibraryVideo = (v: GQLVideo): LibraryVideo => ({
  poster: v.videoThumbnailUrl,
  videoSrc: v.videoUrl,
  downloadUrl: v?.downloadLink ? getDownloadLink(v.downloadLink) : v.videoUrl,
  name: v.title,
  id: v.uuid,
  creatorId: v.creatorId,
  createdAt: v.createdAt,
  fileSize: v.fileSize || 0,
  width: v.width,
  height: v.height,
  duration: v.duration ?? 0,
});

const mediaToVideo = (m: Media): LibraryVideo => ({
  poster: toImgSrc(m),
  videoSrc: toVideoSrc(m),
  downloadUrl: toVideoSrc(m),
  name: m.uuid || '',
  id: m.uuid || '',
  creatorId: 0,
  createdAt: '',
  fileSize: 0,
  width: m.width || 0,
  height: m.height || 0,
  duration: 0,
});

const fullMediaToVideo = (m: FullMedia): LibraryVideo => ({
  poster: toImgSrc(m),
  videoSrc: toVideoSrc(m),
  downloadUrl: toVideoSrc(m),
  name: m.name,
  id: m.id,
  creatorId: m.creatorId,
  createdAt: m.createdAt,
  fileSize: m.fileSize,
  width: m.width || 0,
  height: m.height || 0,
  duration: m.duration ?? 0,
});

const muxToLibraryVideo = (m: MuxVideo): LibraryVideo => {
  const highestQualityAvailable =
    m?.downloadLink?.high ?? m?.downloadLink?.medium ?? m?.downloadLink?.low;

  return {
    poster: m.thumbnail,
    videoSrc: m.video,
    downloadUrl: highestQualityAvailable?.link || '',
    name: m.title,
    id: m.id,
    creatorId: m.influencerId,
    createdAt: m.createdAt,
    fileSize: m.fileSize,
    width: m.width,
    height: m.height,
    duration: m.duration,
  };
};

type VideoMapper =
  | TypedValue<'GQLVideo', GQLVideo>
  | TypedValue<'FullMedia', FullMedia>
  | TypedValue<'Media', Media>
  | TypedValue<'MuxVideo', MuxVideo>;

export const toLibraryVideo =
  <T extends VideoMapper['type']>(t: T) =>
  (v: ValueMap<VideoMapper>[T]): LibraryVideo => {
    switch (t) {
      case 'GQLVideo':
        return gqlToLibraryVideo(v as GQLVideo);
      case 'FullMedia':
        return fullMediaToVideo(v as FullMedia);
      case 'Media':
        return mediaToVideo(v as Media);

      case 'MuxVideo':
      default: // TODO: figure out why ts is complaining about this PM
        return muxToLibraryVideo(v as MuxVideo);
    }
  };

export const muxToBackendExerciseMedia = (
  m: MuxVideo,
): Media & { status: string } => {
  /* This format is saved in Workout.exercises, backwards compatibility handled by Mobile already */
  return {
    image: null,
    cdn01: null,
    video: null,
    source: '',
    width: m.width,
    height: m.height,
    mimeType: 'video/mp4',
    uuid: m.id,
    status: m.status,
    videoUrl: m.video,
    videoThumbnailUrl: m.thumbnail,
    downloadLink: m.downloadLink,
    videoSourceType: 'mux',
  };
};

export const fullMediaToMedia = (m: FullMedia): Media =>
  commonMediaKeys.reduce(
    (acc, key) => ({ ...acc, [key]: m[key] }),
    {} as CommonMedia,
  );

export type MuxVideo = {
  id: string;
  influencerId: number;
  thumbnail: string;
  video: string;
  mimeType: 'application/x-mpegURL';
  width: number;
  height: number;
  uploadLink: string;
  downloadLink?: Download;
  duration: number;
  type: string;
  title: string;
  aspectRatio: string;
  resolution: string;
  status:
    | 'UPLOAD_URL_CREATED'
    | 'PROCESSING_STARTED'
    | 'PROCESSING_ENCODE_FINISHED'
    | 'PROCESSING_FINISHED';
  fileSize: number;
  deletedAt: string | null;
  createdAt: string;
};

export const getAllMuxVideos = async ({
  influencerId,
}: {
  influencerId: number;
}) => {
  const response = await fetch(
    `${import.meta.env.VITE_MUX_API_URL}/library?influencerId=${influencerId}`,
    {
      method: 'GET',
      headers: {
        Authorization: auth.getToken(),
      },
    },
  );

  if (response.status === 401) {
    throw new Error('auth');
  }

  if (!response.ok) {
    throw new Error('Network response error');
  }

  const result = (await response.json()) as { data: MuxVideo[] };

  return result.data.map(muxToLibraryVideo).filter((x) => x.videoSrc);
};

const generateMuxSignedUrl = (data: {
  influencerId: number;
  fileSize: number;
  title: string;
  thumbnailPosition?: string;
  bucketName?: string;
}): Promise<{
  signedURL: string;
  videoId: number;
}> =>
  axios
    .post(`${import.meta.env.VITE_MUX_API_URL}/signed-url`, data, {
      headers: {
        Authorization: auth.getToken(),
      },
    })
    .then((response) => {
      if (response.status === 401) {
        throw new Error('auth');
      }
      return response.data;
    })
    .then((result) => ({
      signedURL: result.signedURL,
      videoId: result.video.id,
    }));

const chunkSize = 5120;

const fetchVideo = async (videoId: number) =>
  axios
    .get<{ message: string; video: MuxVideo }>(
      `${import.meta.env.VITE_MUX_API_URL}/video/${videoId}`,
      {
        headers: {
          Authorization: auth.getToken(),
        },
      },
    )
    .then((res) => res.data);

export const videoToMedia = (v: LibraryVideo): Media => {
  const url = new URL(v.videoSrc);
  const imageUrl = new URL(v.poster);

  const media: Media = {
    image: imageUrl.pathname,
    cdn01: url.origin,
    video: url.pathname,
    width: v.width,
    height: v.height,
  };

  return media;
};

const leadingZeroFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 2,
});

const millisecondsFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 3,
});

export const getVideoTimeFormat = (time: number | undefined) => {
  if (!time) {
    return '0:00';
  }
  const miliseconds = _.round(time - Math.floor(time), 1) * 10;
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${minutes}:${leadingZeroFormatter.format(seconds)}.${miliseconds}`;
  }
  return `${hours}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}`;
};

export const getFfmpegTimeFormat = (time: number | undefined) => {
  if (!time) {
    return '00:00:00';
  }
  const miliseconds = _.round(time - Math.floor(time), 3) * 1000;
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
      minutes,
    )}:${leadingZeroFormatter.format(seconds)}${
      miliseconds > 0 ? '.' + miliseconds : ''
    }`;
  }
  return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}${
    miliseconds > 0 ? '.' + millisecondsFormatter.format(miliseconds) : ''
  }`;
};

/** interpolate between two values with given scale */
export const lerp = (a: number, b: number, t: number) => a + t * (b - a);

export const getFileNameWihoutExtension = (name: string) => {
  const divider = name.lastIndexOf('.');
  return name.substring(0, divider);
};
